import { User } from '../utils/auth'

// Types
export interface AuthResponse {
  success: boolean
  user?: User
  token?: string
  error?: string
  verificationRequired?: boolean
}

export interface VerificationResponse {
  success: boolean
  resetToken?: string
  error?: string
}

// Mock Database
interface DBUser {
  password: string
  user: User
  verificationCode?: string
  verificationCodeExpires?: number
  resetCode?: string
  resetCodeExpires?: number
  resetToken?: string
}

// Initial Mock Data
const USERS: Record<string, DBUser> = {
  jimmy: {
    password: 'jimmyallhere',
    user: {
      id: '1',
      username: 'jimmy',
      email: 'jimmy@allhere.com',
      name: 'Jimmy Smith',
      avatar:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80',
      verified: {
        phone: true,
        id: true,
      },
    },
  },
  bri: {
    password: 'briallhere',
    user: {
      id: '2',
      username: 'bri',
      email: 'bri@allhere.com',
      name: 'Bri Johnson',
      avatar:
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80',
      verified: {
        phone: true,
        id: false,
      },
    },
  },
}

// Session Store
const SESSIONS: Record<string, User> = {}

// Helper to simulate network delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Helper to generate 6-digit code
const generateCode = () =>
  Math.floor(100000 + Math.random() * 900000).toString()

// Helper to generate token
const generateToken = () =>
  Math.random().toString(36).substring(2) + Date.now().toString(36)

export const authBackend = {
  async authenticateUser(
    username: string,
    password: string,
  ): Promise<AuthResponse> {
    await delay(800)

    const normalizedUsername = username.toLowerCase()
    const dbUser = USERS[normalizedUsername]

    if (!dbUser || dbUser.password !== password) {
      return { success: false, error: 'Invalid username or password' }
    }

    // Generate session token
    const token = generateToken()
    SESSIONS[token] = dbUser.user

    return {
      success: true,
      user: dbUser.user,
      token,
    }
  },

  async registerUser(
    username: string,
    email: string,
    password: string,
    name: string,
  ): Promise<AuthResponse> {
    await delay(1000)

    const normalizedUsername = username.toLowerCase()

    if (USERS[normalizedUsername]) {
      return { success: false, error: 'Username already exists' }
    }

    // Check if email is taken (simple check)
    const emailTaken = Object.values(USERS).some(
      (u) => u.user.email.toLowerCase() === email.toLowerCase(),
    )
    if (emailTaken) {
      return { success: false, error: 'Email already registered' }
    }

    // Create new user
    const newUser: User = {
      id: Date.now().toString(),
      username: normalizedUsername,
      email,
      name,
      verified: {
        phone: false,
        id: false,
      },
    }

    // Generate verification code
    const verificationCode = generateCode()

    // Store in DB
    USERS[normalizedUsername] = {
      password,
      user: newUser,
      verificationCode,
      verificationCodeExpires: Date.now() + 10 * 60 * 1000, // 10 minutes
    }

    console.log(
      `[MOCK EMAIL] Verification code for ${email}: ${verificationCode}`,
    )

    return {
      success: true,
      user: newUser,
      verificationRequired: true,
    }
  },

  async validateSession(
    token: string,
  ): Promise<{ valid: boolean; user?: User }> {
    await delay(300)
    const user = SESSIONS[token]
    return { valid: !!user, user }
  },

  async requestPasswordReset(
    email: string,
  ): Promise<{ success: boolean; message?: string }> {
    await delay(800)

    // Find user by email
    const userEntry = Object.entries(USERS).find(
      ([_, data]) => data.user.email.toLowerCase() === email.toLowerCase(),
    )

    if (!userEntry) {
      // For security, don't reveal if email exists
      return {
        success: true,
        message: 'If an account exists, a code has been sent.',
      }
    }

    const [username, data] = userEntry
    const resetCode = generateCode()

    USERS[username] = {
      ...data,
      resetCode,
      resetCodeExpires: Date.now() + 10 * 60 * 1000, // 10 minutes
    }

    console.log(`[MOCK EMAIL] Password reset code for ${email}: ${resetCode}`)

    return { success: true }
  },

  async verifyResetCode(
    email: string,
    code: string,
  ): Promise<VerificationResponse> {
    await delay(600)

    const userEntry = Object.entries(USERS).find(
      ([_, data]) => data.user.email.toLowerCase() === email.toLowerCase(),
    )

    if (!userEntry) {
      return { success: false, error: 'Invalid code' }
    }

    const [username, data] = userEntry

    if (
      data.resetCode !== code ||
      (data.resetCodeExpires && Date.now() > data.resetCodeExpires)
    ) {
      return { success: false, error: 'Invalid or expired code' }
    }

    // Generate reset token
    const resetToken = generateToken()
    USERS[username] = {
      ...data,
      resetCode: undefined,
      resetCodeExpires: undefined,
      resetToken,
    }

    return { success: true, resetToken }
  },

  async resetPassword(
    resetToken: string,
    newPassword: string,
  ): Promise<{ success: boolean; error?: string }> {
    await delay(800)

    const userEntry = Object.entries(USERS).find(
      ([_, data]) => data.resetToken === resetToken,
    )

    if (!userEntry) {
      return { success: false, error: 'Invalid session. Please try again.' }
    }

    const [username, data] = userEntry

    USERS[username] = {
      ...data,
      password: newPassword,
      resetToken: undefined,
    }

    return { success: true }
  },

  async sendVerificationEmail(email: string): Promise<{ success: boolean }> {
    await delay(500)

    const userEntry = Object.entries(USERS).find(
      ([_, data]) => data.user.email.toLowerCase() === email.toLowerCase(),
    )

    if (userEntry) {
      const [username, data] = userEntry
      const code = generateCode()

      USERS[username] = {
        ...data,
        verificationCode: code,
        verificationCodeExpires: Date.now() + 10 * 60 * 1000,
      }

      console.log(`[MOCK EMAIL] Verification code for ${email}: ${code}`)
    }

    return { success: true }
  },

  async verifyEmail(
    email: string,
    code: string,
  ): Promise<{ success: boolean; error?: string }> {
    await delay(600)

    const userEntry = Object.entries(USERS).find(
      ([_, data]) => data.user.email.toLowerCase() === email.toLowerCase(),
    )

    if (!userEntry) {
      return { success: false, error: 'User not found' }
    }

    const [username, data] = userEntry

    if (
      data.verificationCode !== code ||
      (data.verificationCodeExpires &&
        Date.now() > data.verificationCodeExpires)
    ) {
      return { success: false, error: 'Invalid or expired code' }
    }

    // Mark as verified (we'll assume email verification counts as ID verification for this demo, or just a separate flag)
    // For this demo, let's just clear the code. In a real app, we'd update a verifiedEmail flag.
    USERS[username] = {
      ...data,
      verificationCode: undefined,
      verificationCodeExpires: undefined,
    }

    return { success: true }
  },

  async updateUserProfile(
    userId: string,
    updates: {
      name?: string
      avatar?: string
      bio?: string
      verified?: { phone: boolean; id: boolean }
    },
  ): Promise<{ success: boolean; user?: User; error?: string }> {
    await delay(500)

    // Find user by ID
    const userEntry = Object.entries(USERS).find(
      ([_, data]) => data.user.id === userId,
    )

    if (!userEntry) {
      return { success: false, error: 'User not found' }
    }

    const [username, data] = userEntry

    // Update user data
    const updatedUser: User = {
      ...data.user,
      ...(updates.name && { name: updates.name }),
      ...(updates.avatar && { avatar: updates.avatar }),
      ...(updates.verified && { verified: updates.verified }),
    }

    USERS[username] = {
      ...data,
      user: updatedUser,
    }

    console.log('[MOCK API] User profile updated:', updatedUser)

    return { success: true, user: updatedUser }
  },
}
